import React from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { 
  Heart, Star, CheckCircle, UserPlus, X, Search, Send, MapPin, Calendar, 
  Sparkles, Target, Briefcase
} from 'lucide-react';
import { formatTime12h, formatDateMonthDay } from '../../../utils/timeFormat';

const FavoritesTab = ({
  favorites,
  searchQuery,
  setSearchQuery,
  selectedTalent,
  toggleTalentSelection,
  removeFavorite,
  openInviteDialog,
  setShowDirectBookDialog,
  setDirectBookTalentIds,
  setDirectBookShiftId,
  topPicks,
  loadingRecommendations,
  navigate
}) => {
  // Filter favorites by search
  const filteredFavorites = favorites.filter(f => {
    const searchLower = searchQuery.toLowerCase();
    return !searchQuery || 
      f.first_name?.toLowerCase().includes(searchLower) ||
      f.last_name?.toLowerCase().includes(searchLower) ||
      f.city?.toLowerCase().includes(searchLower);
  });

  return (
    <>
      {/* Smart Recommendations - Top Picks */}
      {topPicks.length > 0 && (
        <Card className="bg-gradient-to-br from-[#03DFFC]/5 to-white border-[#03DFFC]/20 shadow-sm mb-6">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-[#03DFFC]/10 rounded-lg">
                <Sparkles className="w-4 h-4 text-[#03DFFC]" />
              </div>
              <CardTitle className="text-base text-gray-900">Smart Matches from Favorites</CardTitle>
              <Badge className="text-[10px] bg-[#03DFFC]/20 text-[#03DFFC] border-[#03DFFC]/30">
                AI Powered
              </Badge>
            </div>
            <p className="text-xs text-gray-500 mt-1">Top talent from your favorites matched to your upcoming gigs</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topPicks.slice(0, 3).map(gig => (
                <div key={gig.gig_id} className="bg-white rounded-lg p-4 shadow-sm" data-testid={`top-picks-gig-${gig.gig_id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-gray-900">{gig.gig_name}</h4>
                      <p className="text-xs text-gray-500">
                        {formatDateMonthDay(gig.shift_date)} at {formatTime12h(gig.start_time)}
                      </p>
                    </div>
                    <Badge className="bg-[#03DFFC]/20 text-[#03DFFC] border-[#03DFFC]/30">
                      {gig.top_picks.length} matches
                    </Badge>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {gig.top_picks.slice(0, 4).map(pick => (
                      <div 
                        key={pick.talent_id}
                        className="flex items-center gap-2 bg-gray-50 rounded-full px-3 py-1 border"
                      >
                        {pick.profile_photo_url ? (
                          <img src={pick.profile_photo_url} alt={pick.name} className="w-6 h-6 rounded-full object-cover" />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px]">
                            {pick.name.split(' ').map(n => n[0]).join('')}
                          </div>
                        )}
                        <span className="text-sm font-medium text-gray-700">{pick.name.split(' ')[0]}</span>
                        <Badge 
                          className={`text-[10px] px-1 py-0 ${
                            pick.match_label === 'Top Match' 
                              ? 'bg-green-100 text-green-700 border-green-300' 
                              : 'bg-blue-100 text-blue-700 border-blue-300'
                          }`}
                        >
                          {pick.match_label === 'Top Match' ? <Target className="w-2.5 h-2.5 mr-0.5 inline" /> : null}
                          {pick.match_label}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { 
                            setDirectBookTalentIds([pick.talent_id]); 
                            setDirectBookShiftId(gig.gig_id);
                            setShowDirectBookDialog(true); 
                          }}
                          className="h-5 px-1 text-green-600 hover:bg-green-50"
                          title="Direct Book"
                        >
                          <Briefcase className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  {gig.top_picks.length > 0 && gig.top_picks[0].reasons && (
                    <div className="mt-2 flex gap-1 flex-wrap">
                      {gig.top_picks[0].reasons.slice(0, 3).map((reason, i) => (
                        <span key={i} className="text-[10px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                          {reason}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-xl text-gray-900">Favorite Talent</CardTitle>
            <div className="flex items-center gap-2">
              {favorites.length > 0 && (
                <Button
                  size="sm"
                  onClick={() => openInviteDialog('favorites')}
                  className="bg-[#FF2D95] hover:bg-[#FF2D95]/90 text-white"
                  data-testid="invite-all-favorites-btn"
                >
                  <Send className="w-3 h-3 mr-1" />
                  Invite All to Job
                </Button>
              )}
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search favorites..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredFavorites.length === 0 ? (
            searchQuery ? (
              <div className="text-center py-10" data-testid="favorites-empty-search">
                <Search className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm font-medium text-gray-700 mb-1">No favorites match "{searchQuery}"</p>
                <p className="text-xs text-gray-500 mb-4">Try a different name, city, or role.</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSearchQuery('')}
                  data-testid="favorites-clear-search-btn"
                >
                  Clear search
                </Button>
              </div>
            ) : (
              <div className="text-center py-10 px-4" data-testid="favorites-empty">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-[#FF2D95]/10 flex items-center justify-center">
                  <Heart className="w-7 h-7 text-[#FF2D95]" />
                </div>
                <p className="text-sm font-semibold text-gray-900 mb-1">No favorites yet</p>
                <p className="text-xs text-gray-500 max-w-sm mx-auto mb-4">
                  Tap the heart icon on any talent in Discover to save them here. Favorites get first dibs on every gig you post.
                </p>
                <Button
                  size="sm"
                  onClick={() => navigate('/client/discover')}
                  className="bg-[#FF2D95] hover:bg-[#FF2D95]/90 text-white"
                  data-testid="favorites-discover-cta"
                >
                  Discover Talent
                </Button>
              </div>
            )
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredFavorites.map((fav) => (
                <div 
                  key={fav.id} 
                  className={`relative flex items-center gap-4 p-4 rounded-lg border transition-all cursor-pointer ${
                    selectedTalent.includes(fav.talent_id)
                      ? 'bg-[#FF2D95]/5 border-[#FF2D95]'
                      : 'bg-gray-50 border-transparent hover:border-gray-200'
                  }`}
                  onClick={() => toggleTalentSelection(fav.talent_id)}
                  data-testid={`favorite-card-${fav.talent_id}`}
                >
                  <div className="absolute top-2 right-2 flex gap-1">
                    {selectedTalent.includes(fav.talent_id) && (
                      <CheckCircle className="w-4 h-4 text-[#FF2D95]" />
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); removeFavorite(fav.talent_id); }}
                      className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                  {fav.profile_photo_url ? (
                    <img 
                      src={fav.profile_photo_url} 
                      alt={fav.first_name}
                      className="w-14 h-14 rounded-full object-cover"
                      onClick={(e) => { e.stopPropagation(); navigate(`/client/discover/${fav.talent_id}`); }}
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-[#03DFFC]/10 flex items-center justify-center">
                      <span className="text-[#03DFFC] font-medium text-lg">
                        {fav.first_name?.[0]}{fav.last_name?.[0]}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 flex items-center gap-2">
                      {fav.first_name} {fav.last_name}
                      {fav.rating && (
                        <span className="flex items-center text-xs text-amber-600">
                          <Star className="w-3 h-3 fill-amber-400 mr-0.5" />
                          {fav.rating}
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {fav.city}, {fav.state}
                    </p>
                    {fav.roles && fav.roles.length > 0 && (
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {fav.roles.slice(0, 2).map((role, i) => (
                          <Badge key={i} variant="outline" className="text-[10px] px-1 py-0 text-gray-600">
                            {role}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Selected Actions */}
          {selectedTalent.length > 0 && (
            <div className="mt-6 flex items-center justify-between p-4 bg-[#FF2D95]/5 rounded-lg border border-[#FF2D95]/20">
              <span className="text-sm text-gray-700">
                <strong>{selectedTalent.length}</strong> talent selected
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openInviteDialog('selected')}
                  className="border-[#FF2D95] text-[#FF2D95] hover:bg-[#FF2D95]/10"
                >
                  <Send className="w-3 h-3 mr-1" />
                  Invite Selected
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {}}
                  className="text-gray-500"
                >
                  Clear Selection
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default FavoritesTab;
